import {Analytics, getAnalytics, isSupported} from 'firebase/analytics';
import {getAuth} from 'firebase/auth';
import {getFunctions} from 'firebase/functions';
import {fetchAndActivate, getRemoteConfig} from 'firebase/remote-config';
import {getStorage} from 'firebase/storage';
import {useEffect, useState} from 'react';
import {
  AnalyticsProvider,
  AuthProvider,
  FirestoreProvider,
  FunctionsProvider,
  RemoteConfigProvider,
  StorageProvider,
  FirebaseAppProvider as WithFirebase,
} from 'reactfire';
import {useSigninStatus} from '../auth/hooks/useSigninStatus';
import {FullScreenLoading} from '../common/Loading';
import {startupProfiler} from '../common/utils/startupProfiler';
import {configuration} from '../config';
import {LoggerProvider, useLogger} from '../logging/LoggerProvider';
import {ComponentWithRequiredChildren, ComponentWithoutChildren, PropsWithRequiredChildren} from '../react/types';
import {firebaseApp, getOrInitializeFirebaseApp} from './firebase';
import {useInitializeFirestore} from './hooks/useInitializeFirestore';
import {PageViewLogger} from './PageViewLogger';

export {getOrInitializeFirebaseApp};

const eagerFirebaseApp = firebaseApp;
const eagerAuth = getAuth(eagerFirebaseApp);
const eagerFunctions = getFunctions(eagerFirebaseApp);
const eagerStorage = getStorage(eagerFirebaseApp);

const eagerRemoteConfig = getRemoteConfig(eagerFirebaseApp);
eagerRemoteConfig.settings = configuration.firebaseRemoteConfiguration;
fetchAndActivate(eagerRemoteConfig).catch(() => {
  // Throws in dev mode or network error; non-fatal
});

// Non-blocking background performance monitoring
import('firebase/performance')
  .then(({getPerformance}) => getPerformance(eagerFirebaseApp))
  .catch(() => {
    // Non-fatal in dev mode or offline
  });

export const FirebaseApp: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  useEffect(() => {
    startupProfiler.mark('Firebase Pre-Auth Features Ready');
    startupProfiler.mark('Firebase Auth Provider Ready');
  }, []);

  // Note: Firebase session expiration/extension is handled in PrivateAppOutlet
  return (
    <WithFirebase firebaseApp={eagerFirebaseApp} suspense>
      <AuthProvider sdk={eagerAuth}>
        <FunctionsProvider sdk={eagerFunctions}>
          <RemoteConfigProvider sdk={eagerRemoteConfig}>
            <StorageProvider sdk={eagerStorage}>
              <LoggerProvider>
                <WithPostLogoutReload>
                  <WithPostAuthFeatures>
                    <WithAnalytics />
                    {children}
                  </WithPostAuthFeatures>
                </WithPostLogoutReload>
              </LoggerProvider>
            </StorageProvider>
          </RemoteConfigProvider>
        </FunctionsProvider>
      </AuthProvider>
    </WithFirebase>
  );
};

const WithAnalytics: ComponentWithoutChildren = () => {
  const [analytics, setAnalytics] = useState<Analytics>();

  useEffect(() => {
    isSupported()
      .then((supported) => {
        if (supported) {
          setAnalytics(getAnalytics(eagerFirebaseApp));
        }
      })
      .catch(() => {
        // Analytics unsupported or blocked; non-fatal
      });
  }, []);

  if (!analytics) {
    return null;
  }

  return (
    <AnalyticsProvider sdk={analytics}>
      <PageViewLogger />
    </AnalyticsProvider>
  );
};

const WithPostLogoutReload: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  const {isSignedIn} = useSigninStatus();
  const [lastSignInStatus, setLastSignInStatus] = useState<boolean>(isSignedIn);

  /*
   * We force the page to reload on logout as a workaround for some reactfire bugs.  See:
   * https://github.com/FirebaseExtended/reactfire/issues/485
   * https://github.com/FirebaseExtended/reactfire/discussions/228
   * https://github.com/FirebaseExtended/reactfire/issues/574
   */

  useEffect(() => {
    if (lastSignInStatus && !isSignedIn) {
      // Login status changed from logged in to logged out - trigger reload
      globalThis.location.reload();
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLastSignInStatus(isSignedIn);
    }
  }, [isSignedIn, lastSignInStatus, setLastSignInStatus]);

  return <>{children}</>;
};

const WithPostAuthFeatures: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  const log = useLogger();
  const {error: firestoreError, firestore} = useInitializeFirestore();

  useEffect(() => {
    if (firestore) {
      startupProfiler.mark('Firestore Ready');
    }
  }, [firestore]);

  if (firestoreError) {
    // Errors during firestore initialization are not necessarily fatal.
    // For example, an error will be returned if the browser does not support offline mode.
    log.error('Error initializing Firestore', firestoreError);
  }

  if (!firestore) {
    return <FullScreenLoading />;
  }

  return <FirestoreProvider sdk={firestore}>{children}</FirestoreProvider>;
};
